import React from "react";
import { Box, Typography, ToggleButton, ToggleButtonGroup, TextField } from "@mui/material";
import { BarChartOutlined } from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";

export type TimeRange = "7d" | "30d" | "90d" | "custom";

interface AnalyticsHeaderProps {
  timeRange: TimeRange;
  onTimeRangeChange: (range: TimeRange) => void;
  customFrom: string;
  customTo: string;
  onCustomFromChange: (val: string) => void;
  onCustomToChange: (val: string) => void;
}

const AnalyticsHeader: React.FC<AnalyticsHeaderProps> = ({
  timeRange,
  onTimeRangeChange,
  customFrom,
  customTo,
  onCustomFromChange,
  onCustomToChange,
}) => {
  const { translate } = useTranslation("operations");

  const handleChange = (_: React.MouseEvent<HTMLElement>, newValue: TimeRange | null) => {
    if (newValue) {
      onTimeRangeChange(newValue);
    }
  };

  const toggleButtonSx = {
    fontFamily: dt.font,
    fontSize: "13px",
    fontWeight: 500,
    textTransform: "none" as const,
    px: 2,
    py: 0.75,
    borderColor: dt.cardBorder,
    color: dt.textSecondary,
    "&.Mui-selected": {
      background: dt.blue,
      color: "#fff",
      borderColor: dt.blue,
      "&:hover": {
        background: dt.blueHover,
      },
    },
    "&:hover": {
      background: dt.blueSoft,
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
        mb: 3,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <BarChartOutlined sx={{ fontSize: 24, color: dt.blue }} />
        <Typography
          sx={{
            fontFamily: dt.font,
            fontSize: "22px",
            fontWeight: 700,
            color: dt.textPrimary,
            letterSpacing: "-0.3px",
          }}
        >
          {translate("analytics.title")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <ToggleButtonGroup
          value={timeRange}
          exclusive
          onChange={handleChange}
          size="small"
          sx={{ borderRadius: dt.radiusSm }}
        >
          <ToggleButton value="7d" sx={toggleButtonSx}>
            {translate("analytics.period.7d")}
          </ToggleButton>
          <ToggleButton value="30d" sx={toggleButtonSx}>
            {translate("analytics.period.30d")}
          </ToggleButton>
          <ToggleButton value="90d" sx={toggleButtonSx}>
            {translate("analytics.period.90d")}
          </ToggleButton>
          <ToggleButton value="custom" sx={toggleButtonSx}>
            {translate("analytics.period.custom")}
          </ToggleButton>
        </ToggleButtonGroup>

        {timeRange === "custom" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              type="date"
              size="small"
              value={customFrom}
              onChange={e => onCustomFromChange(e.target.value)}
              InputProps={{
                sx: {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  borderRadius: dt.radiusSm,
                },
              }}
            />
            <Typography sx={{ fontFamily: dt.font, fontSize: "13px", color: dt.textMuted }}>
              —
            </Typography>
            <TextField
              type="date"
              size="small"
              value={customTo}
              onChange={e => onCustomToChange(e.target.value)}
              InputProps={{
                sx: {
                  fontFamily: dt.font,
                  fontSize: "13px",
                  borderRadius: dt.radiusSm,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnalyticsHeader;
